import { atom } from "jotai";
import { JotaiTool } from "../../../../index";

export const formsSetupToolsAtom = atom<JotaiTool<any>[]>(() => [
  // Tools will be provided by the recipe system when activated
]);
formsSetupToolsAtom.debugLabel = "formsSetupToolsAtom";

export const formsSetupRecipe = `# Forms Setup: Create a form or kanban board that connects to a Zapier Table

## 🚨 CRITICAL: FUNDAMENTAL FORM → TABLE → ZAP WORKFLOW 🚨

**MANDATORY UNDERSTANDING - READ FIRST:**

### Forms CANNOT Directly Trigger Zap workflows
- ❌ **NEVER use storageType: "zap"** - this is INVALID and will fail
- ❌ **Forms cannot directly connect to Zap workflows** - this relationship does not exist
- ✅ **Forms MUST use storageType: "tables"** - this is the ONLY valid storage type

### Required Workflow Pattern: Form → Table → Zap
1. **ALWAYS create Table FIRST** - Forms are useless without a table
2. **Form submits data TO the Table** (using storageId: TABLE_ID)
3. **Zap triggers FROM new Table records** (not from the form)
4. **Workflow: User fills Form → Data goes to Table → Table triggers Zap**

### Field Mapping Format Requirements
- **MUST use tablesFieldKey: "f1", "f2", "f3"** format (NOT field names)
- **Field IDs correspond to table field creation order:**
  - f1 = first field created in table
  - f2 = second field created in table
  - f3 = third field created in table
- **Get table field information FIRST** before mapping form fields

### Mandatory Form Structure Elements
- **submitText**: Required string for submit button (cannot be empty)
- **storageType**: MUST be "tables" (never "zap")
- **storageId**: MUST be valid table ID (get from table creation)
- **triggers**: Array with minimum 1 trigger (notification, navigate, openExternalUrl)

## Overview
Create a form or kanban board that connects to a Zapier Table by using the PATCH endpoint to update page content with form-block or kanban-block elements.

**IMPORTANT: Field blocks must be children of a form-block, not standalone elements in the blocks array.**

## Key API Endpoints

### POST interfaces/api/interfaces/v0/interfaces?create_home_page=true
- **Purpose**: Create a new interface project with a home page
- **Authentication**: Uses session cookies via network_request tool
- **Required Body**: \`{ "name": "Form Name" }\` (name: 1-32 chars, alphanumeric/spaces/hyphens only)
- **Example**: \`network_request({ method: "POST", api: "interfaces", path: "/api/interfaces/v0/interfaces?create_home_page=true", body: { name: "My Interface" } })\`

### PATCH interfaces/api/interfaces/v0/interfaces/{interface_id}/pages/{page_id}
- **Purpose**: Update page content with form blocks and field mappings to table
- **Authentication**: Uses session cookies via network_request tool
- **Example**: \`network_request({ method: "PATCH", api: "interfaces", path: "/api/interfaces/v0/interfaces/{interface_id}/pages/{page_id}" })\`

## ✅ KANBAN MESSAGING RULES ✅

**✅ YOU CAN SAY THESE THINGS FOR KANBAN:**
- ✅ "Configured the kanban to group by [field name]"
- ✅ "Set up card titles to show [field name]"
- ✅ "I've set up the kanban board with grouping by [field] and card titles from [field]"

**❌ DO NOT SAY TO USERS:**
- ❌ "API" or any technical implementation details
- ❌ "Use handoff tool immediately"
- ❌ "You must configure field mappings manually" (only say this if all configuration attempts fail)

**⚠️ IF CONFIGURATION FAILS:**
- Only after ALL attempts to configure fail, explain: "I wasn't able to configure the field mappings automatically. Let me hand you off to the Interfaces editor where you can set this up."

## Implementation Steps

### For Form Setup (MANDATORY SEQUENCE):

#### STEP 1: VERIFY/CREATE TABLE FIRST 🚨
- **Forms cannot exist without tables** - this is non-negotiable
- If no table exists: **CREATE TABLE FIRST** with appropriate fields
- If table exists: **GET TABLE DETAILS** including field IDs and order
- **Remember**: f1 = 1st field created, f2 = 2nd field created, etc.
- **Store table ID** for form connection

#### STEP 2: Create Form Project
1. **Create form project** using POST /api/interfaces/v0/interfaces?create_home_page=true with basic name
2. **Extract projectId and homepageId** from the response
3. **Update canvas** with form details (projectId, pageId)

#### STEP 3: Configure Form with Table Connection
4. **Use PATCH endpoint** to add form content to the page
5. **Structure content** with version 2 and blocks array containing form-block with field-block children
6. **Map form fields to table fields** using EXACT tablesFieldKey format (f1, f2, f3, etc.)
7. **Configure form submission** to storageType: 'tables' and storageId: TABLE_ID (from Step 1)
8. **Add notification trigger** for user feedback (types: 'notification', 'navigate', or 'openExternalUrl')
9. **Create visual connection** between form and table nodes on canvas

#### STEP 4: Explain Zap Workflow to User
10. **Explain**: "Your form now submits data to the table. To trigger Zap workflows, create a Zap that triggers on new table records."
11. **Workflow**: Form submission → Table record → Zap triggers

### For Kanban Setup (MANDATORY SEQUENCE):

#### STEP 1: VERIFY/CREATE TABLE AND GET FIELD INFORMATION 🚨
- **Kanban requires a connected table** - this is non-negotiable
- If no table exists: **CREATE TABLE FIRST** with appropriate fields inside the Canvas
- **Get table field information** - Use GET /tables/{tableId}/fields to identify field IDs for mapping
- **Identify appropriate fields** (REQUIRED before creating kanban):
  - **groupFieldId (REQUIRED)**: Find a Dropdown (labeled_string) field type. Extract the numeric field ID (e.g., if field has id: 3, use 3). If no labeled_string field exists, CREATE ONE FIRST before proceeding.
  - **cardTitleFieldId (REQUIRED)**: Find a Text (string), Long text (text), or Email (email) field type. Extract the numeric field ID.
  - **cardDescriptionFieldId (OPTIONAL)**: Find an appropriate field from allowed types (Text, Long text, Email, Button types, or Dropdown). Extract the numeric field ID.
- **CRITICAL**: You MUST have at least groupFieldId and cardTitleFieldId identified before creating the kanban block. Store these field IDs to use in the PATCH request.

#### STEP 2: Create Form Project
1. **Create form project** using POST /api/interfaces/v0/interfaces?create_home_page=true
2. **Extract projectId and homepageId** from the response
3. **Update canvas** with form details (projectId, pageId)

#### STEP 3: Configure Kanban with Field Mappings
4. **Use PATCH /api/interfaces/v0/interfaces/{interface_id}/pages/{page_id}** to add kanban-block with REQUIRED field mappings:
   - **CRITICAL**: Use the exact endpoint PATCH /api/interfaces/v0/interfaces/{interface_id}/pages/{page_id} - do NOT try POST /pages/{page_id}/blocks or other endpoints.
   - **MUST include groupFieldId**: Use the numeric field ID of the labeled_string field identified in Step 1
   - **MUST include cardTitleFieldId**: Use the numeric field ID of the string/text/email field identified in Step 1
   - **OPTIONAL cardDescriptionFieldId**: Include if a suitable field was identified
   - Format example: groupFieldId: 3, cardTitleFieldId: 1, cardDescriptionFieldId: 2 (use actual numeric IDs from table fields)
5. **Verify configuration worked** - Check if kanban was created successfully with field mappings applied
6. **If configuration fails**: Retry with different field IDs or field combinations. Ensure groupFieldId is always included.
7. **If all attempts fail**: Only then use handoff tool and explain to user
8. **Create visual connection** between interface and table nodes on canvas

## Critical Patterns

### Form Block Structure (MANDATORY FORMAT)
\`\`\`
{
  type: 'form-block',
  config: {
    submitText: 'Submit', // REQUIRED: Cannot be empty string
    storageType: 'tables', // REQUIRED: MUST be 'tables' (NEVER 'zap')
    storageId: 'TABLE_ID_HERE', // REQUIRED: Valid table ID from step 1
    triggers: [{ // REQUIRED: Array with minimum 1 trigger
      type: 'notification', // VALID: notification | navigate | openExternalUrl
      config: {
        message: 'Thank you! Your form has been submitted successfully.'
      }
    }]
  },
  children: [field-blocks] // REQUIRED: Field blocks go here, NOT in main blocks array
}
\`\`\`

### ❌ INVALID Form Configurations (WILL FAIL):
\`\`\`
// ❌ WRONG - storageType: 'zap' is INVALID
storageType: 'zap'

// ❌ WRONG - empty submitText
submitText: ''

// ❌ WRONG - missing triggers array
// triggers: [] (empty array will fail)

// ❌ WRONG - invalid trigger type
triggers: [{ type: 'success-notification' }] // Not a valid type
\`\`\`

### Field Block Structure (EXACT MAPPING FORMAT)
\`\`\`
{
  type: 'field-block',
  config: {
    name: 'fieldName',
    required: true/false,
    label: 'Field Label',
    inputType: 'text'|'email'|'number'|etc,
    tablesFieldKey: 'f1' // CRITICAL: Must match table field order
  }
}
\`\`\`

### 🚨 CRITICAL: Field Mapping Rules
- **tablesFieldKey MUST use "f" + number format**: "f1", "f2", "f3", etc.
- **Numbers correspond to table field CREATION ORDER** (not names):
  - f1 = first field created in the table
  - f2 = second field created in the table
  - f3 = third field created in the table
- **Get table field information BEFORE creating form** to know correct mapping
- **Example**: If table has Email (f1), Name (f2), Message (f3), then:
  \`\`\`
  // Email field in form:
  tablesFieldKey: 'f1'

  // Name field in form:
  tablesFieldKey: 'f2'

  // Message field in form:
  tablesFieldKey: 'f3'
  \`\`\`

### ❌ INVALID Field Mappings (WILL FAIL):
\`\`\`
// ❌ WRONG - using field names instead of f{number}
tablesFieldKey: 'email'
tablesFieldKey: 'name'

// ❌ WRONG - missing 'f' prefix
tablesFieldKey: '1'
tablesFieldKey: '2'

// ❌ WRONG - incorrect format
tablesFieldKey: 'field1'
tablesFieldKey: 'F1'
\`\`\`

### Kanban Block Structure (Complete Configuration)
\`\`\`
{
  type: 'kanban-block',
  config: {
    title: 'Project Board',
    showTitle: true,
    canCreateCards: true,
    canUpdateCards: true,
    canDeleteCards: true,
    canMoveCards: true,
    canMoveColumns: false,
    tableId: 'table-id-here',
    groupFieldId: 3, // REQUIRED: Numeric field ID of labeled_string field for grouping columns 
    cardTitleFieldId: 1, // REQUIRED: Numeric field ID of string/text/email field for card titles
    cardDescriptionFieldId: 2 // OPTIONAL: Numeric field ID for card descriptions
  }
}
\`\`\`

### Page Content Structure
\`\`\`
{
  content: {
    version: '2',
    blocks: [form-block with children OR kanban-block]
  }
}
\`\`\`

## ERROR RECOVERY & PERSISTENCE PROTOCOL

**When Recipe Hash Fails:**
- Recipe hashes can be unreliable - this is normal, not a blocker
- If use_recipe fails, immediately continue with direct API approach
- DO NOT interpret recipe hash failure as "lack of guidance"

**When API Calls Fail (405/404/etc):**
- API errors are normal during discovery - not signs to handoff
- 405 errors typically mean wrong HTTP method or endpoint structure
- MUST try minimum 2-3 different API approaches before considering handoff:
  - Try different HTTP methods (PUT, PATCH instead of POST)
  - Try different endpoint patterns (/blocks, /components, /elements)
  - Search for API examples with different terms

**Search Persistence Rules:**
- MUST try minimum 3 different search terms before concluding no APIs exist:
  - "form components", "add blocks", "create form", "page elements"
  - Try both generic and specific terms
- Most interface tasks CAN be accomplished through APIs - exhaust options first

**Confidence Anchor:**
Interface is designed to be API-driven. Complex form structures are normal and expected through API calls, not indicators that specialized tools are needed.

## Important Notes

### General Interface Setup:
- **Always create form with ?create_home_page=true** to automatically get a page
- **Always create form with a valid name**: Form names can only contain letters, numbers, dashes, and spaces
- **❌ NEVER use "&" in an interface name** - use "and" instead of "&"
- **Use PATCH endpoint to update entire page content** - individual block creation endpoints don't exist
- **Page content must use version '2' format** with blocks array structure
- **Form is useless without a page** - always ensure a page is created
- **When creating an form based off of a table**, there's a special endpoint called "create form from table" that can be more helpful

### 🚨 CRITICAL Form-Specific Notes:

#### TABLE-FIRST MANDATORY RULES:
- **❌ NEVER start with form creation** - ALWAYS create/verify table first
- **❌ NEVER assume a table exists** - verify table existence and get field details
- **❌ NEVER use storageType: "zap"** - forms cannot directly trigger Zap workflows
- **✅ ALWAYS get table ID and field mapping before creating form**
- **✅ ALWAYS explain Form → Table → Zap workflow to user**

#### STRUCTURE REQUIREMENTS:
- **Field blocks MUST be children of form-block** - not standalone elements in blocks array
- **Form blocks MUST have submitText field** - empty string will cause API failure
- **Form blocks MUST have at least one trigger** - empty triggers array will fail
- **Form submission requires both storageType: 'tables' and storageId: TABLE_ID**
- **Map table fields using tablesFieldKey: "f1", "f2", "f3" format** - NOT field names
- **Trigger types must be valid** - use 'notification', 'navigate', or 'openExternalUrl' (NOT 'success-notification')

#### COMMON FAILURE POINTS:
- **Missing table connection**: Form without table = useless form
- **Wrong field mapping**: Using field names instead of f{number} format
- **Invalid storage type**: Using "zap" instead of "tables"
- **Empty required fields**: submitText, triggers array
- **Incorrect workflow explanation**: Not explaining that Zap workflows trigger from table, not form

### Kanban-Specific Notes:
- **Kanban blocks REQUIRE a connected table** - empty kanbans serve no purpose
- **MANDATORY SEQUENCE**: 
  1. Verify table exists and get field information using GET /tables/{tableId}/fields
  2. Identify required field IDs (groupFieldId, cardTitleFieldId, optionally cardDescriptionFieldId)
  3. Create kanban-block with tableId
  4. Configure field mappings via PATCH with groupFieldId and cardTitleFieldId (REQUIRED)
- **Field mapping configuration may not work reliably** - This is an internal limitation, do not mention API or technical details to users
- **If initial configuration fails**: Retry with different field IDs or field combinations
- **Only handoff if ALL attempts fail** - After trying multiple field combinations, if nothing works, then use handoff tool
- **When handing off**: Simply explain "I wasn't able to configure the field mappings automatically. Let me hand you off to the Interfaces editor where you can set this up." - Do NOT mention API limitations
- **Permission settings can be included**: canCreateCards, canUpdateCards, canDeleteCards, canMoveCards
- **Field type requirements**: 
  - groupFieldId: Must be Dropdown (labeled_string) field type
  - cardTitleFieldId: Must be string, text, or email field type
  - cardDescriptionFieldId: Allowed types are Text (string), Long text (text), Email (email), Button (button_continue_zap), Button (button_trigger_zap), Dropdown (labeled_string)
  - cardDetailFields: Array of field IDs. Allowed types include Text (string), Dropdown (multiple_string), Dropdown (labeled_string), Dropdown (multiple_labeled_string), Long text (text), Checkbox (boolean), Number (number), Number (decimal), Email (email), Link (link), Date & Time (datetime), Currency (currency), Phone number (phone_number), Link (multiple_link), Linked Record (linked_record), Linked Record (multiple_linked_record), AI Field (ai_formula), Formula (formula)

## Troubleshooting

### API Validation Errors:
- **"child blocks should be validated in the context of their parent blocks, not directly"**: This 500 error occurs when field blocks are not properly structured as children of form-block. Ensure field blocks are in the children array of the form-block, not in the top-level blocks array
- **"Invalid input" for form-block**: Check that submitText is included and triggers use valid types
- **"Invalid literal value" errors**: Ensure block types match exactly (e.g., 'field-block', 'markdown-text-block')
- **Missing required fields**: Field blocks need name, required, label, and inputType
- **Form block validation**: Must include submitText (string) and triggers (array with minItems: 1)
- **Form name validation**: Must be 1-32 characters, alphanumeric/spaces/hyphens only (pattern: ^[a-zA-Z0-9 -]*$)

### General Issues:
- **405 Method Not Allowed**: Don't use POST /api/interfaces/v0/blocks - use PATCH page content instead
- **Content not updating**: Use version '2' format with blocks array structure

### Form Block Specific:
- **Field mapping issues**: Ensure exact tablesFieldKey format (f + field_id)
- **Form not submitting**: Verify storageType and storageId are properly configured
- **Invalid trigger types**: Use 'notification', 'navigate', or 'openExternalUrl' only
- **Field blocks not recognized**: Make sure field blocks are in the children array of form-block, not top-level blocks

### Kanban Block Specific:
- **kanban:table-does-not-exist**: Verify tableId exists and is accessible
- **kanban:missing-table-id**: tableId is required for kanban blocks
- **Field mapping errors**: If you get errors configuring field mappings, try different field IDs or combinations
- **If all configuration attempts fail**: Only then use handoff tool - do not mention API limitations to users, simply explain that automatic configuration didn't work

### Kanban Setup Validation:
- **Before creating kanban**: Always verify table exists and get field information (use GET /tables/{tableId}/fields)
- **After creating kanban**: ALWAYS attempt field mapping configuration first - try multiple field combinations if needed
- **Only handoff if ALL attempts fail**: After trying different field IDs and combinations, if nothing works, then use handoff tool
- **Table connection**: If no suitable table exists, create one first with appropriate field types (including labeled_string fields for grouping)
`;
